import React from 'react';
import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';

const TermsServiceUserAgreement = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-orange-500 rounded-full mr-3" aria-hidden="true"></div>
            <span className="text-lg font-semibold">Vesta • Legal Center</span>
          </div>
          <BackButton
            onClick={() => window.history.back()}
            variant="link"
            size="small"
            color="#C97B53"
            ariaLabel="Back to Legal Center"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Policy Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Terms of Service / User Agreement
          </h1>
          <p className="text-gray-600 mb-4">
            Effective Date: [Insert Date]
          </p>
          <p className="text-gray-700">
            Welcome to Vesta ("we," "us," "our"). These Terms of Service ("Terms") govern your access to and use of our website, mobile application, APIs, and related services (collectively, the "Platform").
          </p>
          <p className="text-gray-700 mt-4">
            By creating an account, listing products or services, or making a purchase on the Platform, you agree to be bound by these Terms, our Privacy Policy, and all other policies referenced herein.
          </p>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              1. Our Role
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Vesta is a facilitator, not the producer, packager, storer, or shipper of any food or beverage products. We do not prepare or handle any food ourselves.</li>
              <li>All food, beverages, and in-home chef services are provided by independent sellers ("Sellers").</li>
              <li>We provide tools for:
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-6 mt-2">
                  <li>Viewing video listings showcasing food or beverage products and services.</li>
                  <li>Ordering for local delivery or pickup (no shipping).</li>
                  <li>Booking preorders (e.g., custom cakes, catering).</li>
                  <li>Hiring chefs or caterers for in-home meal preparation.</li>
                </ul>
              </li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Eligibility
            </h2>
            <p className="text-gray-700 mb-4">To use our Platform:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>You must be at least 18 years old.</li>
              <li>Sellers must operate in compliance with applicable state and local food laws (cottage food or commercially licensed).</li>
              <li>Chefs or catering service providers must hold valid food safety credentials and comply with all applicable regulations.</li>
              <li>You must be able to form a legally binding contract.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. Account Registration
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>You must create an account to list items or place orders.</li>
              <li>Provide accurate and current information.</li>
              <li>Maintain the security of your login credentials.</li>
              <li>You are responsible for all activity under your account.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Seller Standards
            </h2>
            <p className="text-gray-700 mb-4">Sellers must:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Obtain and maintain all required licenses, permits, and certifications.</li>
              <li>Provide complete and accurate ingredient lists and allergen disclosures.</li>
              <li>Follow applicable labeling laws, including any required cottage food disclaimers.</li>
              <li>Meet all delivery/pickup windows promised to buyers.</li>
              <li>Provide food in safe, consumable condition, following temperature control and safe handling practices.</li>
              <li>Comply with our Seller Policy, Content & Community Guidelines, and all applicable laws.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Buyer Responsibilities
            </h2>
            <p className="text-gray-700 mb-4">Buyers must:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Read all listing details and allergen information before ordering.</li>
              <li>Be available for agreed pickup/delivery times.</li>
              <li>Provide safe premises for in-home chef services.</li>
              <li>Handle food safely after receiving it.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Preorders & Custom Orders
            </h2>
            <p className="text-gray-700 mb-4">For preorders, such as custom cakes or catering:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers may require a non-refundable deposit.</li>
              <li>Cancellations after preparation begins may not be eligible for refunds.</li>
              <li>Buyers and Sellers should communicate clearly about timelines, designs, and expectations.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Delivery & Pickup
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>No shipping is offered through the Platform.</li>
              <li>Delivery is limited to local service areas.</li>
              <li>Sellers or authorized couriers must follow safe handling practices during transport.</li>
              <li>Risk transfers to the Buyer upon confirmed handoff (signature, photo proof, or code verification).</li>
              <li>Pickup orders must be collected within the stated window; uncollected orders may be discarded without refund.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. In-Home Chef & Catering Services
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Chefs and caterers are independent contractors, not employees of Vesta.</li>
              <li>Buyers must ensure safe, clean, and adequately equipped premises for cooking.</li>
              <li>Vesta is not liable for property damage or personal injury related to on-premise services.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Prohibited Items & Activities
            </h2>
            <p className="text-gray-700 mb-4">You may not:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sell items prohibited by our Prohibited Items Policy.</li>
              <li>Misrepresent your identity, credentials, or licensing.</li>
              <li>Engage in off-platform transactions for items listed on Vesta.</li>
              <li>Post misleading or unsafe content.</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Payments & Fees
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Transactions are processed via our third-party payment provider.</li>
              <li>Sellers agree to pay applicable service fees as set out in our Payment & Fee Policy.</li>
              <li>Buyers agree to pay the total amount displayed at checkout.</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Disputes & Refunds
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Most food orders are non-refundable except for safety or quality issues.</li>
              <li>Buyers must first contact the Seller to resolve any disputes.</li>
              <li>If unresolved, disputes may be escalated to Vesta for review.</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. Intellectual Property
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers retain ownership of their content but grant Vesta a license to display and promote it.</li>
              <li>Buyers may not reproduce or redistribute Seller content without permission.</li>
              <li>See our Intellectual Property & DMCA Policy for more details.</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              13. Limitation of Liability
            </h2>
            <p className="text-gray-700 mb-4">Vesta is not liable for:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Illness, injury, or damages resulting from products or services purchased on the Platform.</li>
              <li>Actions or omissions of Sellers or Buyers.</li>
            </ul>
          </section>

          {/* Section 14 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              14. Governing Law & Dispute Resolution
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>These Terms are governed by the laws of [Governing State].</li>
              <li>Disputes will be resolved via binding arbitration or small claims court, except where prohibited by law.</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              15. Changes to These Terms
            </h2>
            <p className="text-gray-700">
              We may update these Terms at any time. Significant changes will be communicated via email or Platform notifications. Continued use of Vesta after updates constitutes acceptance.
            </p>
          </section>

          {/* Section 16 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              16. Privacy & Data Protection
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information.</li>
              <li>By using Vesta, you consent to the collection and use of your information as described in our Privacy Policy.</li>
            </ul>
          </section>

          {/* Section 17 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              17. Termination
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>You may close your account at any time.</li>
              <li>Vesta may suspend or terminate your account for policy violations, illegal activity, or harmful conduct.</li>
              <li>Upon termination, you remain responsible for outstanding obligations.</li>
            </ul>
          </section>

          {/* Section 18 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              18. Indemnification
            </h2>
            <p className="text-gray-700">
              You agree to indemnify and hold Vesta harmless from any claims, losses, or damages arising from your use of the Platform or violation of these Terms.
            </p>
          </section>

          {/* Section 19 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              19. Force Majeure
            </h2>
            <p className="text-gray-700">
              Vesta is not liable for any failure to perform due to circumstances beyond our reasonable control, including but not limited to natural disasters, government actions, or technical failures.
            </p>
          </section>

          {/* Section 20 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              20. Severability
            </h2>
            <p className="text-gray-700">
              If any provision of these Terms is found to be unenforceable or invalid, the remaining provisions will continue to be in full force and effect.
            </p>
          </section>

          {/* Section 21 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              21. Entire Agreement
            </h2>
            <p className="text-gray-700">
              These Terms, together with our Privacy Policy and other applicable policies, constitute the entire agreement between you and Vesta regarding your use of the Platform.
            </p>
          </section>

          {/* Section 22 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              22. Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms, please contact us at:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Email: legal@vesta.com</li>
              <li>Phone: [Insert Phone Number]</li>
              <li>Mailing Address: [Insert Mailing Address]</li>
            </ul>
          </section>

          {/* Final Statement */}
          <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <p className="text-orange-800 text-center font-medium">
              By using Vesta, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/legal"
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            aria-label="Return to Legal Center"
          >
            Return to Legal Center
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TermsServiceUserAgreement; 